import { useState, useEffect, useCallback } from 'react';
import React from 'react';
import { paymentScreenshotAPI } from '../services/paymentScreenshotAPI';

export interface AutoRecognitionResult {
  success: boolean;
  message: string;
  data?: {
    orderNumber?: string;
    merchantOrderNumber?: string;
    amount?: string;
    paymentTime?: string;
    confidence?: number;
    autoApproved?: boolean;
  };
  error?: string;
  extractedAmount?: number;
  coursePrice?: number;
  registrationId?: number; // 添加registrationId
  ocrResult?: {
    autoApproved?: boolean;
    amountMatches?: boolean;
    [key: string]: any;
  };
}

export interface AutoRecognitionStatus {
  isProcessing: boolean;
  progress: number;
  result: AutoRecognitionResult | null;
  error: string | null;
  retryCount: number;
}

/**
 * 增强的自动识别Hook
 * 提供上传后自动触发识别的完整功能
 */
export const useAutoRecognition = () => {
  const [status, setStatus] = useState<AutoRecognitionStatus>({
    isProcessing: false,
    progress: 0,
    result: null,
    error: null,
    retryCount: 0
  });

  const [websocket, setWebsocket] = useState<WebSocket | null>(null);

  /**
   * 监听自动识别事件
   */
  const listenForRecognitionEvents = useCallback(() => {
    const ws = new WebSocket(`ws://localhost:3001/auto-recognition`);
    
    ws.onopen = () => {
      console.log('🔄 已连接自动识别事件流');
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case 'recognition_start':
          setStatus(prev => ({
            ...prev,
            isProcessing: true,
            progress: 0,
            error: null
          }));
          break;
        
        case 'recognition_progress':
          setStatus(prev => ({
            ...prev,
            progress: data.progress
          }));
          break;
        
        case 'recognition_complete':
          setStatus(prev => ({
            ...prev,
            isProcessing: false,
            progress: 100,
            result: {
              success: true,
              message: '识别完成',
              data: data.result
            }
          }));
          break;
        
        case 'recognition_failed':
          setStatus(prev => ({
            ...prev,
            isProcessing: false,
            error: data.error,
            retryCount: prev.retryCount + 1
          }));
          break;
      }
    };

    ws.onclose = () => {
      console.log('❌ 自动识别事件流连接断开');
    };

    ws.onerror = (error) => {
      console.error('WebSocket错误:', error);
    };

    setWebsocket(ws);

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, []);

  /**
   * 上传文件并触发自动识别
   */
  const uploadAndTriggerRecognition = useCallback(async (
    file: File,
    courseId: string,
    onProgress?: (progress: number) => void
  ): Promise<AutoRecognitionResult> => {
    setStatus(prev => ({
      ...prev,
      isProcessing: true,
      progress: 0,
      error: null,
      result: null
    }));

    try {
      // 验证文件
      if (!file.type.startsWith('image/')) {
        throw new Error('请上传图片文件');
      }

      if (file.size / 1024 / 1024 > 5) {
        throw new Error('图片大小不能超过5MB');
      }

      const formData = new FormData();
      formData.append('screenshot', file);
      formData.append('courseId', courseId);

      // 上传文件
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/payment-screenshots/direct-upload-auto`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('📡 响应状态:', response.status, response.statusText);
      const result = await response.json();
      console.log('📋 完整响应:', result);

      if (!response.ok) {
        // 即使HTTP错误，也可能包含registrationId
        return {
          success: false,
          message: result.message || '上传失败',
          error: result.message || `HTTP ${response.status}`,
          registrationId: result.registrationId, // 确保传递registrationId
          extractedAmount: result.extractedAmount,
          coursePrice: result.coursePrice,
          ocrResult: result.ocrResult
        };
      }

      if (!result.success) {
        // 构建更详细的错误消息
        let errorMessage = result.message || '上传失败';
        if (result.extractedAmount && result.coursePrice) {
          errorMessage = `支付金额错误：支付 ¥${result.extractedAmount} ≠ 课程价格 ¥${result.coursePrice}`;
        }
        
        // 设置错误状态
        setStatus(prev => ({
          ...prev,
          isProcessing: false,
          progress: 100,
          error: errorMessage,
          result: null
        }));
        
        return {
          success: false,
          message: result.message || '上传失败',
          error: errorMessage,
          registrationId: result.registrationId,
          extractedAmount: result.extractedAmount,
          coursePrice: result.coursePrice,
          ocrResult: result.ocrResult
        };
      }

      setStatus(prev => ({
        ...prev,
        progress: 50,
        isProcessing: true
      }));

      // 轮询获取识别结果
      if (!result.registrationId) {
        throw new Error('服务器未返回registrationId');
      }
      console.log('registrationId:', result.registrationId);
      const recognitionResult = await pollRecognitionResult(result.registrationId);
      
      console.log('🎯 最终识别结果:', recognitionResult);
      
      setStatus(prev => ({
        ...prev,
        isProcessing: false,
        progress: 100,
        result: recognitionResult
      }));

      return recognitionResult;

    } catch (error) {
      const errorResult: AutoRecognitionResult = {
        success: false,
        message: '上传失败',
        error: error instanceof Error ? error.message : '上传失败'
      };

      setStatus(prev => ({
        ...prev,
        isProcessing: false,
        error: errorResult.error || null,
        retryCount: prev.retryCount + 1
      }));

      return errorResult;
    }
  }, []);

  /**
   * 轮询识别结果
   */
  const pollRecognitionResult = async (registrationId: string): Promise<AutoRecognitionResult> => {
    const maxAttempts = 30; // 最多等待30秒
    let attempts = 0;

    return new Promise((resolve, reject) => {
      const checkStatus = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/payment-screenshots/auto-ocr-status?registrationId=${registrationId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          const data = await response.json();
          console.log('📊 OCR状态检查:', data);

          if (data.ocrProcessed) {
            // 检查金额不匹配的情况
            if (data.amountMismatch || data.registrationFailed) {
              const errorMsg = data.message || `支付金额错误：支付 ¥${data.extractedAmount || 0} ≠ 课程价格 ¥${data.coursePrice || 0}`;
              
              // 设置错误状态
              setStatus(prev => ({
                ...prev,
                isProcessing: false,
                progress: 100,
                error: errorMsg,
                result: null
              }));
              
              resolve({
                success: false,
                message: errorMsg,
                error: errorMsg,
                extractedAmount: data.extractedAmount,
                coursePrice: data.coursePrice,
                ocrResult: {
                  autoApproved: false,
                  amountMatches: false
                }
              });
            } else {
              resolve({
                success: true,
                message: data.autoApproved ? '支付验证成功，报名已完成！' : '识别完成，需要人工审核',
                data: {
                  ...data.extractedInfo,
                  confidence: data.confidence,
                  autoApproved: data.autoApproved
                },
                extractedAmount: data.extractedAmount,
                coursePrice: data.coursePrice,
                ocrResult: {
                  autoApproved: data.autoApproved,
                  amountMatches: data.amountMatches
                }
              });
            }
            return;
          }

          if (attempts >= maxAttempts) {
            reject(new Error('识别超时'));
            return;
          }

          attempts++;
          setStatus(prev => ({
            ...prev,
            progress: 50 + (attempts / maxAttempts * 50)
          }));

          setTimeout(checkStatus, 1000);
        } catch (error) {
          reject(error);
        }
      };

      checkStatus();
    });
  };

  /**
   * 重新触发识别
   */
  const retriggerRecognition = useCallback(async (screenshotId: string): Promise<AutoRecognitionResult> => {
    setStatus(prev => ({
      ...prev,
      isProcessing: true,
      progress: 0
    }));

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auto-trigger/retrigger/${screenshotId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const result = await response.json();

      if (result.success) {
        setStatus(prev => ({
          ...prev,
          isProcessing: false,
          progress: 100
        }));
      }

      return result;
    } catch (error) {
      const errorResult: AutoRecognitionResult = {
        success: false,
        message: '重新触发失败',
        error: error instanceof Error ? error.message : '重新触发失败'
      };

      setStatus(prev => ({
        ...prev,
        isProcessing: false,
        error: errorResult.error || null
      }));

      return errorResult;
    }
  }, []);

  /**
   * 获取识别状态
   */
  const getRecognitionStatus = useCallback(async (): Promise<any> => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auto-trigger/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      return await response.json();
    } catch (error) {
      throw new Error('获取状态失败');
    }
  }, []);

  /**
   * 重置状态
   */
  const resetStatus = useCallback(() => {
    setStatus({
      isProcessing: false,
      progress: 0,
      result: null,
      error: null,
      retryCount: 0
    });
  }, []);

  useEffect(() => {
    // WebSocket服务器不存在，禁用WebSocket连接
    // const cleanup = listenForRecognitionEvents();
    // return cleanup;
    return () => {};
  }, [listenForRecognitionEvents]);

  return {
    status,
    uploadAndTriggerRecognition,
    retriggerRecognition,
    getRecognitionStatus,
    resetStatus,
    websocket
  };
};

/**
 * 使用React Hook的自动识别组件
 */
export const AutoRecognitionProvider = ({ children }: { children: React.ReactNode }) => {
  return React.createElement('div', { className: 'auto-recognition-provider' }, children);
};